import type { Path, UseFormSetError } from 'react-hook-form';

/** Locations webargs nests field errors under. */
export const ERROR_LOCATIONS = ['json', 'form', 'query', 'path'] as const;

export type ApiErrors = { fields: Record<string, string>; general?: string };

function collectMessages(value: unknown): string[] {
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.flatMap(collectMessages);
  if (value != null && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(collectMessages);
  }
  return [];
}

const FALLBACK = 'Something went wrong saving this prescription.';

/** API errors arrive as the parsed response body, not an Error, so name/message are undefined. */
export function parseApiError(error: unknown): ApiErrors {
  // The generated client throws the parsed body directly (`throw jsonError`) and returns it
  // unwrapped as `error` when throwOnError is off, so unwrap only when there is a wrapper.
  const wrapper =
    error != null && typeof error === 'object' && 'error' in error
      ? (error as { error?: unknown }).error
      : undefined;
  const body = wrapper ?? error;

  if (body instanceof Error) return { fields: {}, general: body.message || FALLBACK };

  if (typeof body === 'string') return { fields: {}, general: body };

  if (body == null || typeof body !== 'object') return { fields: {}, general: FALLBACK };

  let record = body as Record<string, unknown>;
  for (const location of ERROR_LOCATIONS) {
    const nested = record[location];
    if (nested != null && typeof nested === 'object') record = nested as Record<string, unknown>;
  }

  const fields: Record<string, string> = {};
  const general: string[] = [];
  for (const [key, value] of Object.entries(record)) {
    // Per-item errors nest by index, so collect every leaf string rather than
    // assuming the value is a flat list of messages.
    const messages = [...new Set(collectMessages(value))];
    if (messages.length === 0) continue;
    if (key === '_schema') general.push(...messages);
    else fields[key] = messages.join(' ');
  }

  return { fields, general: general.length > 0 ? general.join(' ') : undefined };
}

/** The API names fields in snake_case; the form paths are camelCase under `prescription`. */
const FIELD_PATHS: Record<string, string> = {
  prescriber_npi: 'prescription.physician',
  signed_date: 'prescription.signedDate',
  expiration_date: 'prescription.expirationDate',
  items: 'prescription.items',
};

/** Put whatever the server rejected next to the field that caused it, so a round trip
 *  reports the same way client-side validation does. Returns the unplaceable remainder. */
export function applyApiErrors<T extends Record<string, unknown>>(
  errors: ApiErrors,
  setError: UseFormSetError<T>
): string | undefined {
  const unplaced: string[] = [];
  for (const [key, message] of Object.entries(errors.fields)) {
    const path = FIELD_PATHS[key];
    if (path) setError(path as Path<T>, { type: 'server', message });
    else unplaced.push(message);
  }
  return [errors.general, ...unplaced].filter(Boolean).join(' ') || undefined;
}
